import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Alert,
  IconButton,
  Paper,
  Stack,
  alpha,
  useTheme,
  Chip,
  FormControlLabel,
  Switch,
  Checkbox
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  PrivacyTip as PrivacyIcon,
  Security as SecurityIcon,
  DataUsage as DataUsageIcon,
  Cookie as CookieIcon,
  Share as ShareIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

const PrivacyPolicyDialog = ({ open, onClose, onAccept, showConsentOptions = false }) => {
  const [accepted, setAccepted] = useState(false);
  const [readSections, setReadSections] = useState(new Set());
  const [consentSettings, setConsentSettings] = useState({
    analytics: true,
    marketing: false,
    personalizedAds: false,
    dataSharing: true,
  });
  const theme = useTheme();

  const overviewRef = useRef(null);
  const dataCollectionRef = useRef(null);
  const dataUsageRef = useRef(null);
  const dataSharingRef = useRef(null);
  const dataRightsRef = useRef(null);
  const securityRef = useRef(null);
  const additionalRef = useRef(null);

  const scrollToSection = (ref, sectionName) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setReadSections(prev => new Set([...prev, sectionName]));
  };

  const handleConsentChange = (setting, value) => {
    setConsentSettings(prev => ({ ...prev, [setting]: value }));
  };

  const handleAccept = () => {
    if (onAccept) onAccept(showConsentOptions ? consentSettings : null);
    onClose();
  };

  const handleAcceptAll = () => {
    setConsentSettings({
      analytics: true,
      marketing: true,
      personalizedAds: true,
      dataSharing: true,
    });
    setAccepted(true);
  };

  const handleRejectAll = () => {
    setConsentSettings({
      analytics: false,
      marketing: false,
      personalizedAds: false,
      dataSharing: false,
    });
    setAccepted(true);
  };

  const markAllAsRead = () => {
    setReadSections(new Set(['overview', 'dataCollection', 'dataUsage', 'dataSharing', 'dataRights', 'security', 'additional']));
    setAccepted(true);
  };

  const isFullyRead = readSections.size >= 7;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth sx={{ zIndex: 3000 }} PaperProps={{ sx: { borderRadius: 2, height: '90vh', maxHeight: 800 } }}>
      
      {/* Header */}
      <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', py: 2, pr: 8 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PrivacyIcon color="primary" />
          <Box>
            <Typography variant="h5" fontWeight={600}>Privacy Policy</Typography>
            <Typography variant="body2" color="text.secondary">Last updated: June 1st, 2026 | GDPR & CCPA Compliant</Typography>
          </Box>
          {/* <Chip label="GDPR" size="small" color="primary" variant="outlined" />
          <Chip label="CCPA" size="small" color="secondary" variant="outlined" /> */}
        </Box>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', height: '70vh' }}>
          
          {/* LEFT MENU */}
          <Paper elevation={0} sx={{ width: 260, flexShrink: 0, borderRight: 1, borderColor: 'divider', overflow: 'auto', bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>Policy Sections</Typography>
              <Stack spacing={0.5} sx={{ mt: 2 }}>
                {[
                  { icon: <PrivacyIcon fontSize="small" />, label: '1. Overview', ref: overviewRef, key: 'overview' },
                  { icon: <DataUsageIcon fontSize="small" />, label: '2. Data Collection', ref: dataCollectionRef, key: 'dataCollection' },
                  { icon: <ShareIcon fontSize="small" />, label: '3. Data Usage', ref: dataUsageRef, key: 'dataUsage' },
                  { icon: <ShareIcon fontSize="small" />, label: '4. Data Sharing', ref: dataSharingRef, key: 'dataSharing' },
                  { icon: <EditIcon fontSize="small" />, label: '5. Data Rights', ref: dataRightsRef, key: 'dataRights' },
                  { icon: <SecurityIcon fontSize="small" />, label: '6. Security', ref: securityRef, key: 'security' },
                  { icon: <InfoIcon fontSize="small" />, label: '7. Additional Info', ref: additionalRef, key: 'additional' },
                ].map((item) => (
                  <Button key={item.key} startIcon={item.icon} onClick={() => scrollToSection(item.ref, item.key)} fullWidth sx={{ justifyContent: 'flex-start', textTransform: 'none', fontSize: '0.875rem', color: 'text.primary', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) } }}>
                    {item.label}
                    {readSections.has(item.key) && <CheckCircleIcon fontSize="small" color="success" sx={{ ml: 'auto' }} />}
                  </Button>
                ))}
              </Stack>

              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" fontWeight={600}>Reading Progress</Typography>
              <Typography variant="caption" color="text.secondary">Sections read: {readSections.size}/7</Typography>
              <Box sx={{ width: '100%', height: 4, bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 2, mt: 1 }}>
                <Box sx={{ width: `${(readSections.size / 7) * 100}%`, height: '100%', bgcolor: 'primary.main', borderRadius: 2 }} />
              </Box>

              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" fontWeight={600}>Compliance</Typography>
              <Typography variant="caption" color="text.secondary">GDPR | CCPA | PIPEDA</Typography>
              {/* <Button size="small" startIcon={<DownloadIcon />} variant="outlined" fullWidth sx={{ mt: 1 }}>Download PDF</Button> */}
            </Box>
          </Paper>

          {/* RIGHT CONTENT */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            <Alert severity="info" sx={{ mb: 4 }}>This Privacy Policy explains how we handle your personal information. Please review it carefully.</Alert>

            {/* Section 1 */}
            <Box ref={overviewRef} sx={{ scrollMarginTop: 20, mb: 6 }}>
              <Typography variant="h5" gutterBottom color="primary">1. Privacy Policy Overview</Typography>
              <Typography variant="body2" paragraph>This Privacy Policy describes how we collect, use, process, and disclose your information when you use our platform. By using our services, you agree to this policy.</Typography>
              <Typography variant="body2" paragraph>This policy applies to all users including visitors, registered users, and subscribers across all our services and applications.</Typography>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Section 2 */}
            <Box ref={dataCollectionRef} sx={{ scrollMarginTop: 20, mb: 6 }}>
              <Typography variant="h5" gutterBottom color="primary">2. Information We Collect</Typography>
              <Typography variant="body2" paragraph><strong>Personal Information:</strong> Name, email address, phone number, profile information</Typography>
              <Typography variant="body2" paragraph><strong>Professional Information:</strong> Job title, company, industry, professional credentials</Typography>
              <Typography variant="body2" paragraph><strong>Usage Data:</strong> Pages visited, features used, time spent, device information</Typography>
              <Typography variant="body2" paragraph><strong>Cookies & Tracking:</strong> Browser cookies, pixel tags, and similar technologies</Typography>
              <Typography variant="body2" paragraph sx={{ mt: 2 }}><strong>How we collect data:</strong> Direct interactions (account registration), automated technologies (cookies), third-party sources (social media, business partners)</Typography>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Section 3 */}
            <Box ref={dataUsageRef} sx={{ scrollMarginTop: 20, mb: 6 }}>
              <Typography variant="h5" gutterBottom color="primary">3. How We Use Your Information</Typography>
              <Typography variant="body2" paragraph><strong>Service Delivery:</strong> To provide, maintain, and improve our services</Typography>
              <Typography variant="body2" paragraph><strong>Communication:</strong> To send updates, security alerts, and support messages</Typography>
              <Typography variant="body2" paragraph><strong>Analytics:</strong> To understand usage patterns and improve user experience</Typography>
              <Typography variant="body2" paragraph><strong>Legal Compliance:</strong> To comply with legal obligations and protect our rights</Typography>
              <Typography variant="body2" paragraph sx={{ mt: 2 }}><strong>Legal basis for processing:</strong> Your consent, contractual necessity, legal obligations, and legitimate interests</Typography>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Section 4 */}
            <Box ref={dataSharingRef} sx={{ scrollMarginTop: 20, mb: 6 }}>
              <Typography variant="h5" gutterBottom color="primary">4. When We Share Information</Typography>
              <Typography variant="body2" paragraph><strong>Service Providers:</strong> Trusted third parties who assist in platform operations</Typography>
              <Typography variant="body2" paragraph><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</Typography>
              <Typography variant="body2" paragraph><strong>Legal Requirements:</strong> When required by law or to protect rights and safety</Typography>
              <Typography variant="body2" paragraph><strong>With Your Consent:</strong> When you explicitly authorize information sharing</Typography>
              <Typography variant="body2" paragraph sx={{ mt: 2 }}><strong>Important:</strong> We never sell your personal information to third parties for marketing purposes.</Typography>
              <Typography variant="body2" paragraph><strong>International Transfers:</strong> Your information may be transferred to other countries with appropriate safeguards in place.</Typography>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Section 5 */}
            <Box ref={dataRightsRef} sx={{ scrollMarginTop: 20, mb: 6 }}>
              <Typography variant="h5" gutterBottom color="primary">5. Your Privacy Rights</Typography>
              <Typography variant="body2" paragraph><strong>Right to Access:</strong> Request a copy of your personal data</Typography>
              <Typography variant="body2" paragraph><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</Typography>
              <Typography variant="body2" paragraph><strong>Right to Erasure:</strong> Request deletion of your personal data</Typography>
              <Typography variant="body2" paragraph><strong>Right to Portability:</strong> Receive your data in a portable format</Typography>
              <Typography variant="body2" paragraph><strong>Right to Object:</strong> Object to certain data processing activities</Typography>
              <Typography variant="body2" paragraph><strong>Right to Restrict:</strong> Request restriction of data processing</Typography>
              <Typography variant="body2" paragraph sx={{ mt: 2 }}>To exercise these rights, contact us at privacy@example.com. We will respond within 30 days.</Typography>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Section 6 */}
            <Box ref={securityRef} sx={{ scrollMarginTop: 20, mb: 6 }}>
              <Typography variant="h5" gutterBottom color="primary">6. Data Security</Typography>
              <Typography variant="body2" paragraph>We implement SSL/TLS encryption, regular security audits, access controls, data backup procedures, employee training, and encrypted data storage.</Typography>
              <Typography variant="body2" paragraph>We retain personal information only as long as necessary to fulfill purposes, comply with legal obligations, resolve disputes, and enforce agreements.</Typography>
              <Alert severity="warning" sx={{ mt: 2 }}>While we implement robust security measures, no internet transmission is 100% secure. We cannot guarantee absolute security.</Alert>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Section 7 */}
            <Box ref={additionalRef} sx={{ scrollMarginTop: 20, mb: 6 }}>
              <Typography variant="h5" gutterBottom color="primary">7. Additional Information</Typography>
              <Typography variant="body2" paragraph><strong>Cookies:</strong> We use cookies to enhance user experience. You can control preferences through your browser.</Typography>
              <Typography variant="body2" paragraph><strong>Children's Privacy:</strong> Our services are not intended for children under 13. We do not knowingly collect data from children.</Typography>
              <Typography variant="body2" paragraph><strong>Policy Updates:</strong> We may update this policy periodically. Significant changes will be notified via email.</Typography>
              <Typography variant="body2" paragraph><strong>Contact:</strong> For privacy inquiries, contact our Data Protection Officer at dpo@example.com.</Typography>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Consent Settings */}
            {showConsentOptions && (
              <Paper sx={{ mt: 4, p: 3, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="h6">Consent Preferences</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>Customize how your data is used. You can change these settings anytime.</Typography>
                <Stack spacing={2}>
                  <FormControlLabel control={<Switch checked={consentSettings.analytics} onChange={(e) => handleConsentChange('analytics', e.target.checked)} />} label="Analytics Cookies - Help us understand how visitors interact with our platform" />
                  <FormControlLabel control={<Switch checked={consentSettings.marketing} onChange={(e) => handleConsentChange('marketing', e.target.checked)} />} label="Marketing Communications - Receive promotional emails and updates" />
                  <FormControlLabel control={<Switch checked={consentSettings.personalizedAds} onChange={(e) => handleConsentChange('personalizedAds', e.target.checked)} />} label="Personalized Advertising - Show ads based on your interests" />
                  <FormControlLabel control={<Switch checked={consentSettings.dataSharing} onChange={(e) => handleConsentChange('dataSharing', e.target.checked)} />} label="Data Sharing - Allow sharing of anonymized data with trusted partners" />
                </Stack>
                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                  <Button variant="outlined" size="small" onClick={handleAcceptAll}>Accept All</Button>
                  <Button variant="outlined" size="small" onClick={handleRejectAll}>Reject All</Button>
                </Box>
              </Paper>
            )}

            {/* Acceptance */}
            <Paper sx={{ mt: 4, p: 3, border: 1, borderColor: accepted ? 'success.main' : 'divider', borderRadius: 2, bgcolor: accepted ? alpha(theme.palette.success.main, 0.05) : 'transparent' }}>
              <Typography variant="h6">Policy Acknowledgment</Typography>
              {showConsentOptions ? (
                <>
                  <Typography variant="body2" paragraph>By continuing, you acknowledge that you have read and understood this Privacy Policy.</Typography>
                  <FormControlLabel control={<Checkbox checked={accepted} onChange={(e) => setAccepted(e.target.checked)} disabled={!isFullyRead} />} label="I have read and agree to the Privacy Policy" />
                  {!isFullyRead && <Alert severity="warning" sx={{ mt: 2 }}>Please read all sections before accepting. You have read {readSections.size} of 7 sections.</Alert>}
                  <Button variant="outlined" size="small" onClick={markAllAsRead} sx={{ mt: 2 }}>Mark all as read</Button>
                </>
              ) : (
                <Typography variant="body2">By continuing, you acknowledge that you have read and understood this Privacy Policy.</Typography>
              )}
            </Paper>
          </Box>
        </Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">Version 1.0.0 | Effective June 1st, 2026 | © 2026 Helixome. All rights reserved.</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button onClick={onClose} variant="outlined">{showConsentOptions ? 'Cancel' : 'Close'}</Button>
            {showConsentOptions ? (
              <Button onClick={handleAccept} variant="contained" disabled={!accepted || !isFullyRead}>Save Preferences</Button>
            ) : (
              <Button onClick={onClose} variant="contained">I Understand</Button>
            )}
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PrivacyPolicyDialog;